import React, { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import UploadZone from './components/UploadZone';
import Editor from './components/Editor';
import ResultViewer from './components/ResultViewer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BuyCreditsModal from './components/BuyCreditsModal';
import { LowCreditsBanner } from '@rehub/buy-credits';
import AdminPanel from './components/AdminPanel';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';
import EmbedPage from './components/EmbedPage';
import LandingPage from './components/LandingPage';
import StylePickersPage from './components/StylePickersPage';
import ShareDemoPage from './components/ShareDemoPage';
import FluidBackground from './components/FluidBackground';
import PricingBenefitsModal from './components/PricingBenefitsModal';
import { AppState, ImageState, WatermarkSettings, UserProfile } from './types';
import { declutterImage, upscaleImage, PHOTOCLEAR_IMAGE_MODEL } from './services/gemini';
import { matchWhiteBalanceToReference } from './utils/imageAdjustments';
import { drawAiDisclaimer } from './utils/aiDisclaimer';
import { supabase, isConfigured, uploadImageToStorage, checkAdminStatus, getAdminRole, logEvent, checkUserGenerationPermission, createGenerationRecord, updateGenerationRecord, fetchClientIP, applyWelcomeIpGuard, recordSignupAttributionIfPresent } from './services/supabase';
import {
  cataloguePhotoclearPrices,
  creditsForOperation,
  fetchPhotoclearPrices,
  finalizePhotoclearAction,
  releasePhotoclearAction,
  reservePhotoclearAction,
  type PhotoclearPriceMap,
} from './services/photoclearBilling';
import { PHOTOCLEAR_OPERATIONS, PHOTOCLEAR_PRICING_VERSION, quotePhotoclearOperation } from '@rehub/pricing';
import { adoptSharedSession } from '@rehub/supabase/session-sync';
import { persistAiProvenance, getDownloadLabel, resolveClassification, type AIContentClassification } from '@rehub/ai-transparency';
import { Loader2, Sparkles, X, LogIn, UserPlus, Image as ImageIcon, ImagePlus } from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { useTranslation, isSupportedLanguage } from '@rehub/i18n';
import { AUTO_DECLUTTER_PROMPT, APP_VERSION, GEMINI_INPUT_JPEG_QUALITY, hasBlockedStagingIntent } from './constants';
import {
  loadPersistedPhotoplanReturnContext,
  persistPhotoplanReturnContext,
  readPhotoplanReturnContext,
  sendBackToPhotoplan,
  type PhotoplanReturnContext,
} from './services/photoplan-return';
import {
  consumeAuthIntent,
  consumeCtaLocation,
  consumePendingPlan,
  persistAuthEntry,
  persistAuthParamsFromUrl,
  consumeNext,
  readAuthEntry,
} from './lib/auth-entry';
import { WELCOME_CREDITS } from '@rehub/pricing';
import { trackFirstCleanup, trackFirstUpload, trackRegistrationComplete } from './lib/funnel';
import { RehubTracking } from '@rehub/tracking/react';
import type { TrackingRpc } from '@rehub/tracking';

const photoclearTrackingRpc: TrackingRpc = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args);
  return { data, error: error ? { message: error.message } : null };
};

const cropResultToOriginal = async (base64Result: string, restoreRect: {x: number, y: number, width: number, height: number} | null, is360: boolean = false): Promise<string> => {
  if (!restoreRect) return base64Result;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Calculate crop dimensions with rounding for pixel precision
      const targetW = Math.round(img.width * restoreRect.width);
      const targetH = Math.round(img.height * restoreRect.height);
      const startX = Math.round(img.width * restoreRect.x);
      const startY = Math.round(img.height * restoreRect.y);

      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject("Canvas context error");
        return;
      }
      
      // PIXEL-PERFECT: Disable smoothing for 1:1 crop operations (no scaling)
      // This preserves exact pixel data from Gemini's PNG output
      ctx.imageSmoothingEnabled = false;
      
      ctx.drawImage(img, startX, startY, targetW, targetH, 0, 0, targetW, targetH);
      
      if (is360) {
        const finalRatio = targetW / targetH;
        console.log(`360° image cropped - Final ratio: ${finalRatio.toFixed(4)} (target: 2.0000), dimensions: ${targetW}x${targetH}`);
      }
      
      // PIXEL-PERFECT: Use PNG to avoid JPEG compression artifacts
      // Final format conversion happens at download time based on user preference
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = base64Result;
  });
};

// Helper to get input URL from images table
const getInputUrlForJob = async (jobId: string): Promise<string | undefined> => {
  try {
    const { data } = await supabase
      .from('images')
      .select('input_url')
      .eq('id', jobId)
      .single();
    return data?.input_url || undefined;
  } catch {
    return undefined;
  }
};

const is360EquirectangularImage = (width: number, height: number): boolean => {
  const ratio = width / height;
  // 360 equirectangular images are 2:1 aspect ratio (tolerance of ±5%)
  return Math.abs(ratio - 2.0) < 0.1;
};

/**
 * Picks up an `?imageUrl=` query param used by sister apps (e.g. HDR Photo's
 * "Send to Photoclear" button) to pre-load a finished photo straight into the
 * editor for decluttering.
 */
const getIncomingInputImageUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const candidate = params.get('imageUrl');
  if (!candidate) return null;

  if (candidate.startsWith('data:image/') || /^https?:\/\//i.test(candidate)) {
    return candidate;
  }
  return null;
};

const prepareImageForAPI = async (file: File): Promise<{
    imageData: string;
    aspectRatio: string;
    restoreRect: { x: number, y: number, width: number, height: number } | null;
    is360: boolean;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const originalW = img.width;
      const originalH = img.height;
      const ratio = originalW / originalH;
      
      // Check if this is a 360 equirectangular image
      const is360 = is360EquirectangularImage(originalW, originalH);
      
      if (is360) {
        // For 360 images, we need to work around Gemini's limitation
        // Use 21:9 (closest supported ratio at 2.33:1) with padding, then crop back to 2:1
        const targetRatio = 21/9; // 2.33:1 - closest to 2:1
        
        // Add minimal vertical padding to reach 21:9
        const paddedH = originalW / targetRatio;
        const offsetY = (paddedH - originalH) / 2;
        
        const canvas = document.createElement('canvas');
        canvas.width = originalW;
        canvas.height = paddedH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error("Canvas context failed"));
            return;
        }
        
        // PIXEL-PERFECT: Disable smoothing for exact pixel placement
        ctx.imageSmoothingEnabled = false;
        
        // Black padding top and bottom
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, originalW, paddedH);
        ctx.drawImage(img, 0, offsetY, originalW, originalH);
        
        // Store info to crop back to original 2:1 ratio
        const restoreRect = {
          x: 0,
          y: offsetY / paddedH,
          width: 1,
          height: originalH / paddedH
        };
        
        resolve({
          imageData: canvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
          aspectRatio: "21:9", // Use supported ratio
          restoreRect, // Crop back to 2:1 after processing
          is360: true
        });
        return;
      }
      
      // Non-360 images: apply standard padding logic
      const supported = [
        { id: "1:1", val: 1 },
        { id: "3:4", val: 3/4 },
        { id: "4:3", val: 4/3 },
        { id: "9:16", val: 9/16 },
        { id: "16:9", val: 16/9 },
        { id: "21:9", val: 21/9 }
      ];

      const best = supported.reduce((prev, curr) => 
        Math.abs(curr.val - ratio) < Math.abs(prev.val - ratio) ? curr : prev
      );

      let paddedW, paddedH, offsetX, offsetY;
      
      if (ratio > best.val) {
        paddedW = originalW;
        paddedH = originalW / best.val;
        offsetX = 0;
        offsetY = (paddedH - originalH) / 2;
      } else {
        paddedH = originalH;
        paddedW = originalH * best.val;
        offsetX = (paddedW - originalW) / 2;
        offsetY = 0;
      }

      const canvas = document.createElement('canvas');
      canvas.width = paddedW;
      canvas.height = paddedH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
          reject(new Error("Canvas context failed"));
          return;
      }

      // PIXEL-PERFECT: Disable smoothing for exact pixel placement
      ctx.imageSmoothingEnabled = false;

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, paddedW, paddedH);
      ctx.drawImage(img, offsetX, offsetY, originalW, originalH);

      const restoreRect = {
        x: offsetX / paddedW,
        y: offsetY / paddedH,
        width: originalW / paddedW,
        height: originalH / paddedH
      };

      resolve({
        imageData: canvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
        aspectRatio: best.id,
        restoreRect,
        is360: false
      });
    };
    img.onerror = (e) => reject(e);
    img.src = URL.createObjectURL(file);
  });
};

function App() {
  const { t, setLanguage } = useTranslation();
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingBenefitsModal, setShowPricingBenefitsModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showDashboard, setShowDashboard] = useState(false);
  const [pendingDeclutterId, setPendingDeclutterId] = useState<string | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<'admin' | 'superadmin' | 'editor' | null>(null);
  const [appState, setAppState] = useState<AppState>(AppState.INITIALIZING);
  const intentionalSignOutRef = useRef(false);
  const suppressAuthSyncRef = useRef(false);
  const sessionRefreshRef = useRef<string | null>(null);
  const [images, setImages] = useState<ImageState>({
    originalFile: null,
    originalUrl: null,
    processedUrl: null,
    maskDataUrl: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [isUploadingOriginal, setIsUploadingOriginal] = useState(false);
  const [actionPrices, setActionPrices] = useState<PhotoclearPriceMap>(cataloguePhotoclearPrices);
  const [isConsumingCredit, setIsConsumingCredit] = useState(false);
  const billingLock = useRef(false);
  const openBills = useRef<Record<string, number>>({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [lastOutputUrl, setLastOutputUrl] = useState<string | null>(null);
  const [lastAiOperation, setLastAiOperation] = useState<string>('photoclear_auto');
  const [lastAiClassification, setLastAiClassification] = useState<AIContentClassification>('AI_MODIFIED');
  // Cross-app handoff: an image URL handed to us by a sister app (e.g. HDR Photo)
  // via `?imageUrl=`, fetched into the editor as a local File.
  const [pendingIncomingImageUrl, setPendingIncomingImageUrl] = useState<string | null>(null);
  const [photoplanReturn, setPhotoplanReturn] = useState<PhotoplanReturnContext | null>(null);
  const importStartedRef = useRef(false);
  const [isImportingFromExternal, setIsImportingFromExternal] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const candidate = params.get('imageUrl');
    if (!candidate) return false;
    return candidate.startsWith('data:image/') || /^https?:\/\//i.test(candidate);
  });

  useEffect(() => {
    let cancelled = false;
    let callbackTimer: ReturnType<typeof setTimeout> | null = null;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

    const initApp = async () => {
        fetchClientIP().catch(() => {});

        persistAuthParamsFromUrl(window.location.search);

        // Capture a cross-app `?imageUrl=` handoff (e.g. HDR Photo → Photoclear)
        // up front so it survives the SSO/auth early-returns below. We strip the
        // param from the URL so a refresh doesn't re-trigger the import.
        const incomingInputImageUrl = getIncomingInputImageUrl();
        const photoplanCtx = readPhotoplanReturnContext() ?? loadPersistedPhotoplanReturnContext();
        if (photoplanCtx) {
          persistPhotoplanReturnContext(photoplanCtx);
          setPhotoplanReturn(photoplanCtx);
        }
        if (incomingInputImageUrl) {
          setPendingIncomingImageUrl(incomingInputImageUrl);
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('imageUrl');
          cleanUrl.searchParams.delete('returnTo');
          cleanUrl.searchParams.delete('returnOrigin');
          cleanUrl.searchParams.delete('returnPath');
          cleanUrl.searchParams.delete('bookingId');
          cleanUrl.searchParams.delete('assetId');
          cleanUrl.searchParams.delete('source');
          window.history.replaceState({}, document.title, `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
        }

        // Image processing now runs through the `replicate-nano-banana` Supabase
        // Edge Function (auth'd via the user's Supabase session), so the legacy
        // Gemini-key gate is no longer needed — go straight to UPLOAD.
        setAppState(AppState.UPLOAD);

        if (!isConfigured) {
          setAuthLoading(false);
          return;
        }

        if (window.location.pathname === '/dashboard') {
            setShowDashboard(true);
        }

        const isAuthCallback = window.location.pathname === '/auth/callback';
        
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        const hasAuthTokens = hashParams.get('access_token') || queryParams.get('code');
        
        const ssoAccessToken = hashParams.get('sso_access_token');
        const ssoRefreshToken = hashParams.get('sso_refresh_token');
        
        if (ssoAccessToken && ssoRefreshToken) {
          window.history.replaceState({}, document.title, window.location.pathname);

          const acceptSession = (next: Session) => {
            setSession(next);
            setAuthLoading(false);
            fetchProfile(next.user.id);
          };

          try {
            // Hub and most apps pass raw tokens. URLSearchParams already decodes
            // once — a second decodeURIComponent can corrupt a refresh token and
            // make Photoclear the only app that fails the shared SSO hand-off.
            const { data, error } = await supabase.auth.setSession({
              access_token: ssoAccessToken,
              refresh_token: ssoRefreshToken,
            });

            if (data.session && !cancelled) {
              acceptSession(data.session);
              return;
            }

            const safeDecode = (value: string) => {
              try { return decodeURIComponent(value); } catch { return value; }
            };
            const decodedAccess = safeDecode(ssoAccessToken);
            const decodedRefresh = safeDecode(ssoRefreshToken);
            if (decodedAccess !== ssoAccessToken || decodedRefresh !== ssoRefreshToken) {
              const retry = await supabase.auth.setSession({
                access_token: decodedAccess,
                refresh_token: decodedRefresh,
              });
              if (retry.data.session && !cancelled) {
                acceptSession(retry.data.session);
                return;
              }
              if (retry.error) console.warn('SSO session failed:', retry.error.message);
            } else if (error) {
              console.warn('SSO session failed:', error.message);
            }
          } catch (err) {
            console.warn('SSO session error:', err);
          }
          // Fall through to cookie/localStorage session if SSO hand-off fails.
        }

        if (isAuthCallback) {
          callbackTimer = setTimeout(() => {
            if (!cancelled && window.location.pathname === '/auth/callback') {
              console.warn('Auth callback timeout - redirecting to home');
              window.history.replaceState({}, document.title, "/");
              setCurrentPath("/");
              setAuthLoading(false);
            }
          }, 8000);
        }

        if (hasAuthTokens && isAuthCallback) {
          const code = queryParams.get('code');
          if (code) {
            try {
              const { data, error } = await supabase.auth.exchangeCodeForSession(code);
              if (cancelled) return;
              if (data.session && !error) {
                if (callbackTimer) { clearTimeout(callbackTimer); callbackTimer = null; }
                window.history.replaceState({}, document.title, '/');
                setCurrentPath('/');
                setSession(data.session);
                setAuthLoading(false);
                fetchProfile(data.session.user.id, false);
                return;
              }
              if (error) console.warn('Auth code exchange failed:', error.message);
            } catch (err) {
              console.warn('Auth code exchange error:', err);
            }
          }
          // detectSessionInUrl / onAuthStateChange may still complete it
          return;
        }

        try {
          const { data: { session: existingSession } } = await supabase.auth.getSession();
          if (cancelled) return;

          if (!existingSession) {
            if (!isAuthCallback) setAuthLoading(false);
            return;
          }

          // Validate the JWT server-side to catch expired/revoked tokens
          const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser();
          if (cancelled) return;

          if (validatedUser && !userError) {
            setSession(existingSession);
            setAuthLoading(false);
            if (isAuthCallback || window.location.pathname === '/login') {
              const next = consumeNext();
              if (next === '/dashboard') {
                window.history.replaceState({}, document.title, '/dashboard');
                setCurrentPath('/dashboard');
                fetchProfile(validatedUser.id, true);
              } else {
                window.history.replaceState({}, document.title, '/');
                setCurrentPath('/');
                fetchProfile(validatedUser.id, false);
              }
            } else {
              fetchProfile(validatedUser.id);
            }
          } else {
            // getUser() failed. This is usually a transient network error, not
            // a revoked token — keep the stored session instead of forcing a
            // refresh-token rotation here. Forced rotations invalidate the
            // token every sibling *.rehub.studio app is holding and were
            // causing refresh storms that hit Supabase's /token rate limit
            // (429) and hard-logged users out of Photoclear. If the token is
            // genuinely invalid, supabase-js's own auto-refresh (properly
            // locked) will discover it and emit SIGNED_OUT, which the
            // recovery handler below deals with.
            setSession(existingSession);
            setAuthLoading(false);
            fetchProfile(existingSession.user.id);
          }
        } catch {
          if (!cancelled && !isAuthCallback) setAuthLoading(false);
        }
    };

    const bootTimer = setTimeout(() => {
      if (!cancelled) {
        console.warn('Auth boot timeout — releasing sign-in spinner');
        setAuthLoading(false);
      }
    }, 8000);

    initApp();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (cancelled) return;

      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED': {
          setSession(newSession);
          if (newSession) setAuthLoading(false);
          if (callbackTimer) { clearTimeout(callbackTimer); callbackTimer = null; }
          // A real sign-in/refresh cancels any pending spurious SIGNED_OUT recovery.
          if (recoveryTimer) { clearTimeout(recoveryTimer); recoveryTimer = null; }

          if (newSession) {
            const pendingConsent = localStorage.getItem('pending_marketing_consent');
            if (pendingConsent === 'true') {
              localStorage.removeItem('pending_marketing_consent');
              await supabase
                .from('profiles')
                .update({
                  marketing_consent: true,
                  marketing_consent_at: new Date().toISOString()
                })
                .eq('id', newSession.user.id);
            } else if (pendingConsent === 'false') {
              localStorage.removeItem('pending_marketing_consent');
            }

            setShowLoginModal(false);

            if (event === 'SIGNED_IN') {
              void recordSignupAttributionIfPresent();
              const intent = consumeAuthIntent();
              const cta = consumeCtaLocation();
              const createdAt = new Date(newSession.user.created_at).getTime();
              const isNewUser = Number.isFinite(createdAt) && Date.now() - createdAt < 15 * 60 * 1000;
              if (intent === 'signup' && isNewUser) {
                trackRegistrationComplete(newSession.user.id, cta);
              }
            }

            const pendingRef = localStorage.getItem('referral_code');
            if (pendingRef) {
              localStorage.removeItem('referral_code');
              const provider = newSession.user?.app_metadata?.provider;
              const authMethod =
                provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
              supabase
                .rpc('apply_referral_code', {
                  p_code: pendingRef,
                  p_auth_method: authMethod,
                  p_app_source: 'photoclear',
                })
                .catch(() => {});
            }

            if (pendingFile) {
              fetchProfile(newSession.user.id);
              const file = pendingFile;
              setPendingFile(null);
              setShowLoginPrompt(false);
              setTimeout(() => {
                const url = URL.createObjectURL(file);
                setImages({
                  originalFile: file,
                  originalUrl: url,
                  processedUrl: null,
                  maskDataUrl: null,
                });
                setAppState(AppState.EDITOR);
              }, 100);
              return;
            }

            const path = window.location.pathname;
            if (path === '/auth/callback' || path === '/login') {
              const next = consumeNext();
              if (next === '/dashboard') {
                window.history.replaceState({}, document.title, '/dashboard');
                setCurrentPath('/dashboard');
                fetchProfile(newSession.user.id, true);
              } else {
                window.history.replaceState({}, document.title, '/');
                setCurrentPath('/');
                fetchProfile(newSession.user.id, false);
              }
            } else if (path === '/dashboard') {
              fetchProfile(newSession.user.id, true);
            } else {
              fetchProfile(newSession.user.id);
            }
          }
          break;
        }

          case 'SIGNED_OUT': {
          if (intentionalSignOutRef.current) {
            intentionalSignOutRef.current = false;
            setSession(null);
            setUserProfile(null);
            setIsAdmin(false);
            setAdminRole(null);
            setAuthLoading(false);
            setShowDashboard(false);
            setShowAdminPanel(false);
            break;
          }
          // Spurious sign-out (sibling app rotated shared cookie / aborted
          // refresh). Never call refreshSession here — that burns the token
          // family. Adopt whatever fresher session is still in the cookie.
          if (recoveryTimer) clearTimeout(recoveryTimer);
          recoveryTimer = setTimeout(async () => {
            const MAX_ATTEMPTS = 4;
            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
              if (cancelled) return;
              try {
                const adopted = await adoptSharedSession(supabase, sessionRefreshRef.current);
                if (adopted?.access_token) {
                  setSession(adopted);
                  setAuthLoading(false);
                  fetchProfile(adopted.user.id);
                  return;
                }
              } catch { /* retry */ }
              await new Promise((r) => setTimeout(r, 2000));
            }
            if (cancelled) return;
            setSession(null);
            setUserProfile(null);
            setIsAdmin(false);
            setAdminRole(null);
            setAuthLoading(false);
            setShowDashboard(false);
            setShowAdminPanel(false);
          }, 400);
          break;
        }

        case 'INITIAL_SESSION': {
          if (newSession) {
            setSession(newSession);
          }
          break;
        }
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(bootTimer);
      if (callbackTimer) clearTimeout(callbackTimer);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      subscription.unsubscribe();
    };
  }, []);

  // When returning to Photoclear after using another *.rehub.studio app,
  // adopt that app's fresher shared-cookie session instead of refreshing
  // with a stale in-memory refresh token (which kicks you out).
  useEffect(() => {
    sessionRefreshRef.current = session?.refresh_token ?? null;
  }, [session?.refresh_token]);

  useEffect(() => {
    if (!isConfigured) return;
    let busy = false;
    const sync = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      if (intentionalSignOutRef.current || suppressAuthSyncRef.current) return;
      busy = true;
      try {
        const adopted = await adoptSharedSession(supabase, sessionRefreshRef.current);
        if (suppressAuthSyncRef.current) return;
        if (adopted?.access_token) {
          setSession((prev) =>
            prev?.refresh_token === adopted.refresh_token ? prev : adopted,
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!/abort/i.test(msg)) {
          console.warn('[auth] visibility sync failed:', err);
        }
      } finally {
        busy = false;
      }
    };
    const onFocus = () => { void sync(); };
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (!session?.user) return;

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.new) {
              setUserProfile(payload.new as UserProfile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session]);

  // Keep currentPath in sync with browser back/forward navigation.
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // The admin panel is URL-driven (/admin/<section>) so each section can be
  // bookmarked, shared, and survives a page refresh instead of dropping back
  // to the main screen.
  useEffect(() => {
    setShowAdminPanel(currentPath.startsWith('/admin'));
  }, [currentPath]);

  const navigateTo = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    setCurrentPath(path);
  };

  const handleOpenAdmin = () => navigateTo('/admin');

  useEffect(() => {
    if (!userProfile) return;
    const plan = consumePendingPlan();
    if (!plan) return;
    setPendingPlan(plan);
    setShowBuyCreditsModal(true);
  }, [userProfile]);

  const fetchProfile = async (userId: string, showDashboardAfter: boolean = false) => {
    try {
      // Profile + admin role are required for a usable session UI. Partner status is
      // best-effort — an affiliates RLS/error must not wipe admin or look logged-out.
      const affiliateLookup = Promise.race([
        supabase.from('affiliates').select('id').eq('user_id', userId).maybeSingle().then(
          (res) => res,
          () => ({ data: null, error: null })
        ),
        new Promise<{ data: null; error: null }>((resolve) =>
          setTimeout(() => resolve({ data: null, error: null }), 3000)
        ),
      ]);

      const [profileResult, role, affiliateResult] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        getAdminRole(userId),
        affiliateLookup,
      ]);

      if (profileResult.error) throw profileResult.error;

      setUserProfile(profileResult.data);
      if (isSupportedLanguage(profileResult.data?.language)) {
        setLanguage(profileResult.data.language);
      }
      setAdminRole(role);
      setIsAdmin(!!role);
      setIsPartner(!!affiliateResult.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      try {
        const [{ data: retryData }, role] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', userId).single(),
          getAdminRole(userId),
        ]);
        if (retryData) {
          setUserProfile(retryData);
          setAdminRole(role);
          setIsAdmin(!!role);
        }
      } catch (retryError) {
        console.error('Retry fetching profile also failed:', retryError);
      }
    } finally {
      setAuthLoading(false);
      if (showDashboardAfter) {
        setShowDashboard(true);
      }
    }
  };

  const handleLogout = async () => {
    try {
      intentionalSignOutRef.current = true;
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
      intentionalSignOutRef.current = false;
    }
  };

  const handleOpenDashboard = () => {
      if (session && userProfile) {
          setShowDashboard(true);
      } else {
          setAuthMode('login');
          setShowLoginModal(true);
      }
  };

  // When a sister app (e.g. HDR Photo) hands us an `?imageUrl=`, fetch the blob
  // and load it into the editor as a local File. Fetching to a local File (rather
  // than pointing the canvas at the remote URL) avoids CORS-tainting the canvas,
  // which would otherwise block the toDataURL() exports the editor needs.
  useEffect(() => {
    if (!pendingIncomingImageUrl) return;
    if (importStartedRef.current) return;
    importStartedRef.current = true;

    const remoteUrl = pendingIncomingImageUrl;

    (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      try {
        const res = await fetch(remoteUrl, { credentials: 'omit', signal: controller.signal });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const blob = await res.blob();
        const mime = blob.type || 'image/jpeg';
        const ext = (mime.split('/')[1] || 'jpg').split(';')[0];
        const file = new File([blob], `from-hdrphoto-${Date.now()}.${ext}`, { type: mime });
        const objectUrl = URL.createObjectURL(file);

        setShowDashboard(false);
        setImages((prev) => ({
          ...prev,
          originalFile: file,
          originalUrl: objectUrl,
          processedUrl: null,
          maskDataUrl: null,
        }));
        setAppState(AppState.EDITOR);
      } catch (err) {
        console.error('[Photoclear import] Failed to import incoming image URL:', err);
        alert('Could not load the image from HDR Photo. Please upload it manually.');
        setAppState(AppState.UPLOAD);
      } finally {
        clearTimeout(timeoutId);
        setIsImportingFromExternal(false);
        setPendingIncomingImageUrl(null);
      }
    })();
    // Only depend on `pendingIncomingImageUrl`; the ref guards against re-runs.
  }, [pendingIncomingImageUrl]);

  const handleImageSelected = (file: File, intensityId?: string) => {
    setPendingDeclutterId(intensityId ?? null);
    // Check if user is logged in
    if (!session) {
      setPendingFile(file);
      setShowLoginPrompt(true);
      return;
    }

    // Probe the image so we can (a) detect 360° equirectangular photos and
    // (b) fail loudly if the browser can't actually read the file's bytes
    // (common when the source is an "online-only" cloud file, e.g. Dropbox/
    // iCloud/OneDrive, that hasn't been downloaded locally yet).
    const img = new Image();
    const probeUrl = URL.createObjectURL(file);
    let settled = false;

    const goToEditor = () => {
      setShowDashboard(false);
      const url = URL.createObjectURL(file);
      setImages((prev) => ({
        ...prev,
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null,
      }));
      setAppState(AppState.EDITOR);
      URL.revokeObjectURL(probeUrl);
      if (session?.user) trackFirstUpload(session.user.id);
    };

    const failToLoad = (reason: string) => {
      if (settled) return;
      settled = true;
      console.error('[Photoclear] Could not read selected image:', reason, file.name);
      URL.revokeObjectURL(probeUrl);
      // Stay on the upload screen — never drop the user into a black editor.
      alert(
        "We couldn't open this image.\n\n" +
        "If it's stored in a cloud folder (Dropbox, iCloud, OneDrive, Google Drive), " +
        "make sure the file is fully downloaded to this device, then try again.\n\n" +
        "Otherwise the file may be corrupted or in an unsupported format."
      );
    };

    img.onload = () => {
      if (settled) return;
      settled = true;
      const is360 = is360EquirectangularImage(img.width, img.height);

      if (is360) {
        alert(
          "🌐 360° Equirectangular Image Detected\n\n" +
          "Straighten stays disabled to protect the panorama projection.\n\n" +
          "You can open the immersive 360° viewer, or try experimental AI declutter with brush masking — then preview the result in the 360° viewer before downloading.\n\n" +
          "Edits can still affect seam wrap or platform compatibility, so treat results as experimental."
        );
      }

      goToEditor();
    };

    img.onerror = () => failToLoad('decode error');

    // Guard against the browser hanging indefinitely while a cloud file tries
    // to hydrate — surface a clear error instead of an endless spinner.
    setTimeout(() => failToLoad('timeout'), 20000);

    img.src = probeUrl;
  };

  // Handle proceeding after login with pending file
  const goToAuth = (mode: 'login' | 'signup', cta?: string) => {
    persistAuthEntry({
      mode,
      next: '/',
      ...(cta ? { cta } : {}),
    });
    const path = mode === 'signup' ? '/login?mode=signup' : '/login';
    window.history.pushState({}, '', path);
    setCurrentPath('/login');
  };

  const handleLoginPromptAuth = (mode: 'login' | 'signup') => {
    persistAuthEntry({ mode, next: '/', cta: 'upload_gate' });
    setShowLoginPrompt(false);
    setAuthMode(mode);
    setShowLoginModal(true);
  };

  const consumeCredit = async (): Promise<'success' | 'insufficient_credits' | 'error'> => {
    return 'error';
  };

  useEffect(() => {
    if (!session?.user) return;
    fetchPhotoclearPrices().then(setActionPrices).catch(() => {});
  }, [session?.user?.id]);

  const applyBalance = (balance: number | null, deltaIfMissing: number) => {
    setUserProfile((prev) => {
      if (!prev) return prev;
      const credits = balance == null ? prev.credits + deltaIfMissing : balance;
      return { ...prev, credits };
    });
  };

  const beginBill = async (operation: string): Promise<{ refId: string; credits: number; feature: string } | null> => {
    if (!session?.user) return null;
    if (billingLock.current) return null;
    const quote = quotePhotoclearOperation(operation);
    if (!quote) {
      alert('This edit is not available.');
      return null;
    }
    const quotedCost = actionPrices[quote.feature]?.credits ?? quote.creditCost;
    const guardedBalance = await applyWelcomeIpGuard();
    const balance = guardedBalance ?? userProfile?.credits ?? 0;
    if (guardedBalance != null && guardedBalance !== userProfile?.credits) {
      applyBalance(guardedBalance, 0);
    }
    if (balance < quotedCost) {
      setShowBuyCreditsModal(true);
      return null;
    }
    billingLock.current = true;
    const reserved = await reservePhotoclearAction({
      userId: session.user.id,
      operation,
      quotedCost,
    });
    if (reserved.ok === false) {
      billingLock.current = false;
      if (reserved.error === 'STALE_QUOTE') {
        const next = await fetchPhotoclearPrices();
        setActionPrices(next);
        alert('The price for this edit has changed. Check the updated price, then try again.');
      } else if (reserved.error === 'INSUFFICIENT_CREDITS') {
        setShowBuyCreditsModal(true);
      } else if (reserved.error === 'UNKNOWN_ACTION') {
        alert('This edit is not available.');
      } else {
        alert(reserved.message || 'Could not charge credits. Please try again.');
      }
      return null;
    }
    applyBalance(reserved.balanceAfter, -reserved.creditCost);
    openBills.current[reserved.refId] = reserved.creditCost;
    return { refId: reserved.refId, credits: reserved.creditCost, feature: reserved.quote.feature };
  };

  const settleBill = async (refId: string, success: boolean, credits: number): Promise<boolean> => {
    if (!session?.user) {
      billingLock.current = false;
      return false;
    }
    if (success) {
      await finalizePhotoclearAction(session.user.id, refId);
      delete openBills.current[refId];
      billingLock.current = false;
      return true;
    }
    const refunded = await releasePhotoclearAction(session.user.id, refId);
    billingLock.current = false;
    if (refunded == null) return false;
    if (refunded > 0) applyBalance(null, refunded);
    delete openBills.current[refId];
    return true;
  };

  // --- CONTINUE EDITING ---
  const handleContinueEditing = (file: File) => {
    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null
    });
    setAppState(AppState.EDITOR);
  };

  // --- AUTO PROCESS ---
  const handleAutoProcess = async (file: File) => {
    if (!userProfile) return;
    
    // Prevent double-click race condition
    if (isConsumingCredit) {
        console.log('Blocked duplicate auto-process call');
        return;
    }
    setIsConsumingCredit(true);
    const bill = await beginBill('photoclear_auto');
    if (!bill) {
      setIsConsumingCredit(false);
      return;
    }

    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null
    });
    setAppState(AppState.PROCESSING);
    suppressAuthSyncRef.current = true;
    
    // 2. Generate Job ID for Gallery Pairing
    const jobId = crypto.randomUUID();
    
    // 3. Create generation record for tracking
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: 'auto', job_id: jobId, source_app: 'photoclear', ai_operation: 'photoclear_auto', feature: bill.feature, credits: bill.credits, pricing_version: PHOTOCLEAR_PRICING_VERSION, reservation_ref: bill.refId }
    );
    setLastAiOperation('photoclear_auto');
    setLastAiClassification(resolveClassification({ app: 'photoclear', operation: 'photoclear_auto' }));

    // 4. TRY BLOCK FOR PROCESS & UPLOAD
    let inputUrl: string | undefined;
    try {
        // Upload Original
        if (session?.user) {
             inputUrl = await uploadImageToStorage(session.user.id, file, 'original', jobId);
             if (inputUrl) {
await supabase.from('images').upsert({
                    id: jobId,
                    user_id: session.user.id,
                    input_url: inputUrl,
                    output_url: '',
                    status: 'processing',
                    type: 'restoration',
                    service_type: 'photoclear'
                });
             }
        }

        const { imageData, aspectRatio, restoreRect, is360 } = await prepareImageForAPI(file);
        
        // Show warning for 360 images
        if (is360) {
          console.log('Processing 360 equirectangular image using 21:9 workaround, will crop back to 2:1');
        }
        
        // Client-side Gemini processing
        const resultBase64 = await declutterImage(imageData, AUTO_DECLUTTER_PROMPT, aspectRatio);
        const cropped = await cropResultToOriginal(resultBase64, restoreRect, is360);
        // Neutralize the slight chromatic cast Gemini's image model can introduce
        // by matching the result's white balance back to the original input.
        // Wrapped in try/catch so a WB failure never blocks completion.
        let croppedResult = cropped;
        try {
            const refUrl = URL.createObjectURL(file);
            croppedResult = await matchWhiteBalanceToReference(cropped, refUrl);
            URL.revokeObjectURL(refUrl);
        } catch (wbErr) {
            console.warn('White-balance match skipped:', wbErr);
        }

        // Upload Result and Update DB
        let outputUrl: string | undefined;
        if (session?.user) {
             outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
             if (outputUrl) {
                await supabase.from('images').update({
                    output_url: outputUrl,
                    status: 'completed',
                    type: 'restoration'
                }).eq('id', jobId);
             }
        }

        if (!croppedResult) {
            throw new Error('Processing returned empty result');
        }
        
        // Update generation record to completed with input and output URLs
        if (generationId) {
            await updateGenerationRecord(generationId, 'completed', bill.credits, undefined, outputUrl, inputUrl);
            try {
              await persistAiProvenance({
                supabase,
                target: 'generations',
                id: generationId,
                app: 'photoclear',
                operation: 'photoclear_auto',
                sourceAssetId: inputUrl || jobId,
                outputAssetId: outputUrl || jobId,
                provider: 'google',
                model: PHOTOCLEAR_IMAGE_MODEL,
              });
            } catch (e) {
              console.warn('AI provenance persist failed (non-fatal):', e);
            }
        }
        if (session?.user) {
          try {
            await persistAiProvenance({
              supabase,
              target: 'images',
              id: jobId,
              app: 'photoclear',
              operation: 'photoclear_auto',
              sourceAssetId: inputUrl || jobId,
              outputAssetId: outputUrl || jobId,
              provider: 'google',
              model: PHOTOCLEAR_IMAGE_MODEL,
            });
          } catch (e) {
            console.warn('AI provenance persist (images) failed (non-fatal):', e);
          }
        }
        await settleBill(bill.refId, true, bill.credits);
        
        // Processing complete - go back to Dashboard
        setIsConsumingCredit(false);
        setAppState(AppState.UPLOAD);
        setTimeout(() => setShowDashboard(true), 200);
    } catch (err: any) {
        console.error("Auto Process failed:", err);
        
        const refundSucceeded = await settleBill(bill.refId, false, bill.credits);
        if (session?.user) {
          await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
        }
        if (generationId) {
            await updateGenerationRecord(generationId, 'failed', 0, err.message);
        }

        setError(refundSucceeded
            ? t('photoclear.errorProcessingRefunded')
            : t('photoclear.errorProcessingNoRefund'));
        setAppState(AppState.EDITOR);
        setIsConsumingCredit(false);
    } finally {
      suppressAuthSyncRef.current = false;
    }
  };

  // --- MANUAL PROCESS ---
  const handleProcess = async (
    maskedImageData: string, 
    prompt: string, 
    aspectRatio: string, 
    restoreRect: {x: number, y: number, width: number, height: number} | null,
    is360?: boolean,
    source?: 'preset' | 'custom',
    operation?: string
  ) => {
    if (source === 'custom' && hasBlockedStagingIntent(prompt)) {
      alert('Furniture and full staging edits are available in Virtual Staging. Use "Send to Virtual Staging" from your gallery.');
      return;
    }

    const aiOperation = operation || (source === 'custom' ? 'photoclear_custom' : 'photoclear_auto');
    const aiClassification = resolveClassification({ app: 'photoclear', operation: aiOperation });
    setLastAiOperation(aiOperation);
    setLastAiClassification(aiClassification);
    if (isConsumingCredit || billingLock.current) return;
    setIsConsumingCredit(true);
    const bill = await beginBill(aiOperation);
    if (!bill) {
        setIsConsumingCredit(false);
        return;
    }

    setAppState(AppState.PROCESSING);
    setError(null);
    suppressAuthSyncRef.current = true;

    const jobId = crypto.randomUUID();
    
    // Create generation record
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: 'manual', job_id: jobId, prompt: prompt?.substring(0, 100), source_app: 'photoclear', ai_operation: aiOperation, feature: bill.feature, credits: bill.credits, pricing_version: PHOTOCLEAR_PRICING_VERSION, reservation_ref: bill.refId }
    );

    let inputUrl: string | undefined;
    try {
      if (session?.user && images.originalFile) {
          inputUrl = await uploadImageToStorage(session.user.id, images.originalFile, 'original', jobId);
          if (inputUrl) {
await supabase.from('images').upsert({
                  id: jobId,
                  user_id: session.user.id,
                  input_url: inputUrl,
                  output_url: '',
                  status: 'processing',
                  type: 'restoration',
                  service_type: 'photoclear'
              });
          }
      }

      // Show warning for 360 images
      if (is360) {
        console.warn('Processing 360 equirectangular image - aspect ratio preserved');
      }
      
      // Client-side Gemini processing
      const resultBase64 = await declutterImage(maskedImageData, prompt, aspectRatio);
      const cropped = await cropResultToOriginal(resultBase64, restoreRect, is360);
      // Neutralize Gemini's slight chromatic cast by matching the result's
      // white balance back to the original. Skipped silently on failure.
      let croppedResult = cropped;
      try {
          const refSource = images.originalFile
              ? URL.createObjectURL(images.originalFile)
              : images.originalUrl;
          if (refSource) {
              croppedResult = await matchWhiteBalanceToReference(cropped, refSource);
              if (images.originalFile) URL.revokeObjectURL(refSource);
          }
      } catch (wbErr) {
          console.warn('White-balance match skipped:', wbErr);
      }

      let outputUrl: string | undefined;
      if (session?.user) {
           outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
           if (outputUrl) {
                await supabase.from('images').update({
                    output_url: outputUrl,
                    status: 'completed',
                    type: 'restoration'
                }).eq('id', jobId);
           }
      }

      if (!croppedResult) {
          throw new Error('Processing returned empty result');
      }
      
      // Update generation to completed with input and output URLs
      if (generationId) {
          await updateGenerationRecord(generationId, 'completed', bill.credits, undefined, outputUrl, inputUrl);
          try {
            await persistAiProvenance({
              supabase,
              target: 'generations',
              id: generationId,
              app: 'photoclear',
              operation: aiOperation,
              sourceAssetId: inputUrl || jobId,
              outputAssetId: outputUrl || jobId,
              provider: 'google',
              model: PHOTOCLEAR_IMAGE_MODEL,
            });
          } catch (e) {
            console.warn('AI provenance persist failed (non-fatal):', e);
          }
      }
      if (session?.user) {
        try {
          await persistAiProvenance({
            supabase,
            target: 'images',
            id: jobId,
            app: 'photoclear',
            operation: aiOperation,
            sourceAssetId: inputUrl || jobId,
            outputAssetId: outputUrl || jobId,
            provider: 'google',
            model: PHOTOCLEAR_IMAGE_MODEL,
          });
        } catch (e) {
          console.warn('AI provenance persist (images) failed (non-fatal):', e);
        }
      }
      await settleBill(bill.refId, true, bill.credits);
      setLastOutputUrl(outputUrl ?? null);
      
      setImages(prev => ({
          ...prev,
          maskDataUrl: maskedImageData,
          processedUrl: croppedResult
      }));
      
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
      if (session?.user) trackFirstCleanup(session.user.id);
    } catch (err: any) {
      console.error("Process failed:", err);

      const refundSucceeded = await settleBill(bill.refId, false, bill.credits);
      if (session?.user) {
          await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
      }
      if (generationId) {
          await updateGenerationRecord(generationId, 'failed', 0, err?.message);
      }
      
      setIsConsumingCredit(false);

      setError(refundSucceeded
          ? t('photoclear.errorProcessingRefunded')
          : t('photoclear.errorProcessingNoRefund'));
      setAppState(AppState.EDITOR);
    } finally {
      suppressAuthSyncRef.current = false;
    }
  };

  const handleUpscale = async () => {
    if (!images.processedUrl) return;
    
    setIsUpscaling(true);
    try {
      const upscaledBase64 = await upscaleImage(images.processedUrl);
      // Keep colors consistent across the upscale step.
      let finalUpscaled = upscaledBase64;
      try {
          finalUpscaled = await matchWhiteBalanceToReference(upscaledBase64, images.processedUrl);
      } catch (wbErr) {
          console.warn('Upscale white-balance match skipped:', wbErr);
      }

      setImages(prev => ({
        ...prev,
        processedUrl: finalUpscaled
      }));
    } catch (err: any) {
      console.error(err);
      alert("Upscaling failed. Please try again.");
    } finally {
      setIsUpscaling(false);
    }
  };

  const handleAccept = (imageUrlToUse?: string) => {
    const url = imageUrlToUse ?? images.processedUrl;
    if (url) {
      setImages(prev => ({
        ...prev,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null
      }));
      setAppState(AppState.EDITOR);
      setIsUpscaling(false);
    }
  };

  const handleReset = () => {
    if (images.originalUrl) URL.revokeObjectURL(images.originalUrl);
    setImages({
      originalFile: null,
      originalUrl: null,
      processedUrl: null,
      maskDataUrl: null
    });
    setAppState(AppState.UPLOAD);
    setError(null);
    setIsUpscaling(false);
  };

  const downloadCurrentOriginal = () => {
    if (!images.originalUrl) return;
    const link = document.createElement('a');
    link.href = images.originalUrl;
    link.download = `Photoclear.ai-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openSisterApp = async (base: string, imageUrl: string) => {
    const params = new URLSearchParams({ imageUrl, source: 'photoclear' });
    if (photoplanReturn) {
      params.set('returnTo', 'photoplan');
      params.set('returnOrigin', photoplanReturn.returnOrigin);
      params.set('bookingId', photoplanReturn.bookingId);
      params.set('assetId', photoplanReturn.assetId);
    }
    let hash = '';
    if (isConfigured) {
      try {
        const { data: { session: activeSession } } = await supabase.auth.getSession();
        if (activeSession?.access_token && activeSession?.refresh_token) {
          hash = `#sso_access_token=${encodeURIComponent(activeSession.access_token)}&sso_refresh_token=${encodeURIComponent(activeSession.refresh_token)}`;
        }
      } catch (_) {}
    }
    window.open(`${base}/?${params.toString()}${hash}`, '_blank', 'noopener,noreferrer');
  };

  const handleSendToHyperdusk = async (imageUrl: string) => {
    await openSisterApp('https://hyperdusk.rehub.studio', imageUrl);
  };

  const handleSendToPhotoEdit = async (imageUrl: string) => {
    await openSisterApp('https://photoedit.rehub.studio', imageUrl);
  };

  const handleSendToPhotoplan = async (imageUrl: string) => {
    if (!photoplanReturn) return;
    let url = imageUrl;
    if (!/^https:\/\//i.test(url) && session?.user) {
      const uploaded = await uploadImageToStorage(session.user.id, url, 'output');
      if (!uploaded) {
        alert('Could not save the result to send back to Photoplan.');
        return;
      }
      url = uploaded;
    }
    if (!/^https:\/\//i.test(url)) {
      alert('Could not send this result back to Photoplan. Try again after processing finishes.');
      return;
    }
    sendBackToPhotoplan(photoplanReturn, url);
  };

  const handleSendToVirtualStaging = async (imageUrl: string) => {
    await openSisterApp('https://staging.rehub.studio', imageUrl);
  };

  const handleDownload = async (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null, sourceUrl?: string, aiDisclaimer?: boolean) => {
    const processedUrl = sourceUrl ?? images.processedUrl;
    
    if (!processedUrl) {
      console.error('No processed image URL available for download');
      return;
    }
    
    console.log('Starting download:', { format, hasWatermark: !!watermark, aiDisclaimer: !!aiDisclaimer, urlType: processedUrl.startsWith('data:') ? 'base64' : 'remote' });
    
    // Helper to trigger download from a data URL or blob URL
    const triggerDownload = (url: string, filename: string) => {
      console.log('Triggering download...');
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Small delay before removing to ensure download starts
      setTimeout(() => document.body.removeChild(link), 100);
    };
    
    const filename = `Photoclear.ai-${Date.now()}.${format}`;
    
    // Quick path: If it's base64, no watermark/disclaimer, and format matches - download directly
    if (processedUrl.startsWith('data:') && !watermark && !aiDisclaimer) {
      const isJpeg = processedUrl.includes('image/jpeg') || processedUrl.includes('image/jpg');
      const isPng = processedUrl.includes('image/png');
      
      if ((format === 'jpeg' && isJpeg) || (format === 'png' && isPng)) {
        console.log('Direct base64 download (format matches)');
        triggerDownload(processedUrl, filename);
        return;
      }
    }
    
    // For all other cases, use canvas to process/convert the image
    const img = new Image();
    
    // Only set crossOrigin for remote URLs, not for data URLs
    if (!processedUrl.startsWith('data:')) {
      img.crossOrigin = "anonymous";
    }
    
    img.onload = () => {
      console.log('Image loaded for processing:', img.width, 'x', img.height);
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Failed to get canvas context');
        // Fallback: try direct download
        triggerDownload(processedUrl, filename);
        return;
      }
      
      ctx.drawImage(img, 0, 0);

      const finishDownload = () => {
        if (aiDisclaimer) {
          drawAiDisclaimer(ctx, img.width, img.height, getDownloadLabel(lastAiClassification));
        }
        try {
          const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
          const quality = format === 'jpeg' ? 0.95 : undefined;
          const dataUrl = canvas.toDataURL(mimeType, quality);
          console.log('Canvas converted, downloading...');
          triggerDownload(dataUrl, filename);
        } catch (e) {
          console.error('Canvas toDataURL failed:', e);
          // Fallback: try direct download
          triggerDownload(processedUrl, filename);
        }
      };

      if (watermark) {
        const logo = new Image();
        logo.onload = () => {
          const logoWidth = img.width * watermark.scale;
          const logoHeight = logoWidth * (logo.height / logo.width);
          const logoX = img.width * watermark.x;
          const logoY = img.height * watermark.y;
          ctx.globalAlpha = watermark.opacity;
          ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
          ctx.globalAlpha = 1.0;
          finishDownload();
        };
        logo.onerror = () => {
          console.warn('Watermark logo failed to load, downloading without watermark');
          finishDownload();
        };
        logo.src = watermark.imageUrl;
      } else {
        finishDownload();
      }
    };
    
    img.onerror = (e) => {
      console.error('Image failed to load for download:', e);
      
      // Fallback: try fetch + blob approach for remote URLs
      if (!processedUrl.startsWith('data:')) {
        console.log('Trying fetch fallback...');
        fetch(processedUrl)
          .then(response => response.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            triggerDownload(blobUrl, filename);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          })
          .catch(fetchError => {
            console.error('Fetch download also failed:', fetchError);
            // Last resort: open in new tab
            window.open(processedUrl, '_blank');
          });
      } else {
        // For data URLs that somehow failed, try direct download anyway
        triggerDownload(processedUrl, filename);
      }
    };
    
    img.src = processedUrl;
  };

  // Public share demo page - no auth required
  const shareMatch = currentPath.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
  if (shareMatch) {
    return <ShareDemoPage slug={shareMatch[1]} />;
  }

  // Embeddable page routes - render without main app shell
  const isEmbedRoute = currentPath === '/embed' || currentPath === '/pricing' || currentPath === '/signup';
  if (isEmbedRoute) {
     const isIframe = window.self !== window.top;
     return <EmbedPage isEmbed={isIframe} />;
  }

  // Hold the boot spinner for the whole auth resolve (SSO / cookie / getUser).
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black flex-col gap-4">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        <p className="text-slate-400 animate-pulse">Signing you in…</p>
      </div>
    );
  }

  const authEntry = readAuthEntry(typeof window !== 'undefined' ? window.location.search : '');
  const wantsDirectAuth =
    currentPath === '/login' ||
    (currentPath === '/' && (authEntry.mode === 'signup' && new URLSearchParams(window.location.search).get('signup') === '1'));

  if (currentPath === '/styles') {
    return (
      <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
        <FluidBackground />
        <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <Header
            onReset={() => navigateTo('/')}
            showNewPhoto={false}
            user={userProfile}
            onLogin={() => goToAuth('login', 'app_header')}
            onSignUp={() => goToAuth('signup', 'app_header')}
            onOpenDashboard={session ? handleOpenDashboard : () => goToAuth('login', 'app_header')}
            isAdmin={isAdmin}
            onOpenAdmin={handleOpenAdmin}
          />
          <StylePickersPage
            onSignUp={session ? () => navigateTo('/') : () => goToAuth('signup', 'app_styles')}
          />
          <div className="shrink-0">
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  if (!session && (currentPath === '/login' || wantsDirectAuth)) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        <Login initialMode={authEntry.mode} />
      </div>
    );
  }

  // Landing page only after auth has finished and there is still no session
  if (!session && currentPath === '/') {
    return (
      <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
        <RehubTracking app="photoclear" mode="events-only" userId={null} rpc={photoclearTrackingRpc} />
        <FluidBackground />
        <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <Header
            onReset={() => {}}
            showNewPhoto={false}
            user={null}
            onLogin={() => goToAuth('login', 'app_header')}
            onSignUp={() => goToAuth('signup', 'app_header')}
            onOpenDashboard={() => goToAuth('login', 'app_header')}
            isAdmin={isAdmin}
            onOpenAdmin={handleOpenAdmin}
          />
          <LandingPage
            onSignUp={() => goToAuth('signup', 'app_landing')}
            onLogin={() => goToAuth('login', 'app_landing')}
            onOpenPricing={() => setShowPricingBenefitsModal(true)}
          />
          <div className="shrink-0">
            <Footer />
          </div>
          {showPricingBenefitsModal && (
            <PricingBenefitsModal
              onClose={() => setShowPricingBenefitsModal(false)}
              onSignUp={() => {
                setShowPricingBenefitsModal(false);
                goToAuth('signup', 'app_pricing_modal');
              }}
            />
          )}
          {showLoginModal && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="w-full max-w-lg">
                  <Login
                    onClose={() => setShowLoginModal(false)}
                    initialMode={authMode}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-black min-h-screen">
      <RehubTracking app="photoclear" mode="events-only" userId={session?.user?.id ?? null} rpc={photoclearTrackingRpc} />
      {isImportingFromExternal && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-slate-900/90 backdrop-blur-md border border-orange-500/40 text-slate-200 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm">
          <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
          <span>Importing image from HDR Photo…</span>
        </div>
      )}
<Header
        onReset={handleReset}
        showNewPhoto={appState !== AppState.UPLOAD && appState !== AppState.INITIALIZING}
        user={userProfile}
        onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
        onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
        onOpenDashboard={handleOpenDashboard}
        onOpenBuyCredits={() => setShowBuyCreditsModal(true)}
        onNavigateToSettings={handleOpenDashboard}
        onNavigateToMembers={handleOpenDashboard}
        onNavigateToReferrals={() => setShowReferrals(true)}
        isAdmin={isAdmin}
        onOpenAdmin={handleOpenAdmin}
        onLogout={handleLogout}
      />
      {userProfile && (
        <div className="px-4 pt-3">
          <LowCreditsBanner
            balance={userProfile.credits ?? 0}
            onBuy={() => setShowBuyCreditsModal(true)}
          />
        </div>
      )}
      {/* Scrollable column: main content + footer so footer sits below upload panel (scroll to see it) */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
      <main className="flex-1 min-h-[calc(100vh-4.5rem)] overflow-hidden relative">
        {appState === AppState.INITIALIZING && (
           <div className="flex items-center justify-center h-full">
             <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
           </div>
        )}

        {appState === AppState.UPLOAD && (
          <>
            <FluidBackground />
            <div className="relative z-10 flex-1 flex min-h-full items-center justify-center w-full">
              <div className="w-full max-w-4xl mx-auto">
                <UploadZone onImageSelected={handleImageSelected} />
              </div>
            </div>
          </>
        )}

        {appState === AppState.PROCESSING && images.originalUrl && !images.maskDataUrl ? (
            <div className="flex flex-col items-center justify-center h-full gap-8 bg-black animate-in fade-in duration-500">
                 <div className="relative">
                     <div className="absolute inset-0 bg-orange-500/20 blur-xl rounded-full animate-pulse"></div>
                     <Loader2 className="w-16 h-16 text-orange-500 animate-spin relative z-10" />
                 </div>
                 <div className="text-center space-y-2">
                     <h2 className="text-2xl font-bold text-slate-100">Restoring Your Photo</h2>
                     <p className="text-slate-400 max-w-sm">
                         Our AI is analyzing the scene and decluttering your image. 
                         This usually takes 10-20 seconds.
                     </p>
                 </div>
                 <button
                   type="button"
                   onClick={() => window.open(window.location.origin + '/', '_blank')}
                   className="flex items-center gap-2.5 px-5 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-medium text-sm shadow-lg shadow-orange-500/25 transition-colors"
                 >
                   <ImagePlus size={20} />
                   Edit another image in new tab
                 </button>
                 <p className="text-slate-500 text-xs max-w-xs text-center">
                   Open multiple tabs to edit several images at once while each processes.
                 </p>
            </div>
        ) : (
            (appState === AppState.EDITOR || appState === AppState.PROCESSING) && images.originalUrl && (
              <>
                <Editor 
                  imageUrl={images.originalUrl} 
                  onProcess={handleProcess}
                  isProcessing={appState === AppState.PROCESSING}
                  onDownload={downloadCurrentOriginal}
                  onOpenDashboard={handleOpenDashboard}
                  pendingDeclutterId={pendingDeclutterId}
                  actionCredits={Object.fromEntries(
                    PHOTOCLEAR_OPERATIONS.map((row) => [
                      row.operation,
                      creditsForOperation(row.operation, actionPrices) ?? row.creditCost,
                    ])
                  )}
                  onReserveAction={async (operation) => {
                    const reserved = await beginBill(operation);
                    return reserved?.refId ?? null;
                  }}
                  onFinishAction={async (refId, success) => {
                    const credits = openBills.current[refId] ?? actionPrices.straighten_image?.credits ?? 1;
                    await settleBill(refId, success, credits);
                  }}
                />
                {error && (
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-6 py-3 rounded-full shadow-lg font-medium animate-bounce border border-red-400">
                    {error}
                  </div>
                )}
                {isUploadingOriginal && (
                    <div className="absolute bottom-6 right-6 z-50 bg-slate-800/80 text-slate-300 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-md border border-slate-700">
                        <Loader2 size={12} className="animate-spin" />
                        Saving to cloud...
                    </div>
                )}
              </>
            )
        )}

      </main>

      {/* Footer in scroll flow below main so it doesn't overlap upload panel */}
      {!showDashboard && (
        <div className="shrink-0">
          <Footer />
        </div>
      )}
      </div>

      {/* Result Viewer - rendered as overlay to ensure visibility */}
      {appState === AppState.RESULT && (
        images.processedUrl ? (
          <div className="fixed inset-0 z-40 bg-black">
            <ResultViewer
              originalUrl={images.originalUrl || images.processedUrl}
              processedUrl={images.processedUrl}
              onBack={() => setAppState(AppState.EDITOR)}
              onAccept={handleAccept}
              onDownload={handleDownload}
              userId={session?.user?.id}
              aiOperation={lastAiOperation}
              aiClassification={lastAiClassification}
              onSendToHyperdusk={lastOutputUrl ? () => handleSendToHyperdusk(lastOutputUrl) : undefined}
              onSendToVirtualStaging={lastOutputUrl ? () => handleSendToVirtualStaging(lastOutputUrl) : undefined}
              onSendToPhotoEdit={lastOutputUrl ? () => handleSendToPhotoEdit(lastOutputUrl) : undefined}
              onSendToPhotoplan={
                photoplanReturn && (lastOutputUrl || images.processedUrl)
                  ? () => handleSendToPhotoplan(lastOutputUrl || images.processedUrl!)
                  : undefined
              }
              sendToPhotoplanLabel={
                photoplanReturn?.returnPath ? 'Save to Photoplan gallery' : undefined
              }
            />
          </div>
        ) : (
          <div className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center gap-4">
            <p className="text-red-400 text-lg">Error: Result image not found</p>
            <p className="text-slate-400 text-sm">The processed image was not saved correctly.</p>
            <button 
              onClick={() => {
                setAppState(AppState.UPLOAD);
                setShowDashboard(true);
              }}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-500 font-medium"
            >
              Go to Gallery
            </button>
          </div>
        )
      )}

      {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                  <div className="w-full max-w-lg">
                      <Login
                        onClose={() => setShowLoginModal(false)}
                        initialMode={authMode}
                      />
                  </div>
              </div>
          </div>
      )}

      {/* Animated Login Prompt for non-logged-in users who upload */}
      {showLoginPrompt && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
              <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-300">
                  {/* Close button */}
                  <button 
                    onClick={() => { setShowLoginPrompt(false); setPendingFile(null); }}
                    className="absolute -top-2 -right-2 z-10 p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                  
                  {/* Card */}
                  <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700 shadow-2xl">
                      {/* Logo */}
                      <div className="flex justify-center mb-6">
                          <div className="relative">
                              <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full"></div>
                              <img 
                                src="/Photoclear Logo HR Trans.png" 
                                alt="Photoclear" 
                                className="relative h-16 w-auto drop-shadow-lg"
                              />
                          </div>
                      </div>
                      
                      {/* Image preview thumbnail */}
                      {pendingFile && (
                          <div className="flex justify-center mb-4">
                              <div className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-slate-600 shadow-lg">
                                  <img 
                                    src={URL.createObjectURL(pendingFile)} 
                                    alt="Uploaded" 
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                  <ImageIcon className="absolute bottom-1 right-1 w-4 h-4 text-white/70" />
                              </div>
                          </div>
                      )}
                      
                      {/* Text */}
                      <div className="text-center mb-8">
                          <h2 className="text-2xl font-bold text-white mb-2">
                              Ready to Transform!
                          </h2>
                          <p className="text-slate-400">
                              Sign in to start editing your photo with AI-powered decluttering.
                          </p>
                          <p className="text-blue-200 text-sm mt-2 font-medium">
                              {WELCOME_CREDITS} free credits on sign-up. No card required.
                          </p>
                      </div>
                      
                      {/* Buttons */}
                      <div className="space-y-3">
                          <button
                            onClick={() => handleLoginPromptAuth('signup')}
                            className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-cyan-500 hover:brightness-110 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-blue-500/25"
                          >
                            <UserPlus size={20} />
                            Create Free Account
                          </button>
                          
                          <button
                            onClick={() => handleLoginPromptAuth('login')}
                            className="w-full py-4 px-6 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <LogIn size={20} />
                            Sign In
                          </button>
                      </div>
                      
                      {/* Fine print */}
                      <p className="text-center text-slate-500 text-xs mt-6">
                          Your image is saved and will be ready after you sign in.
                      </p>
                  </div>
              </div>
          </div>
      )}

      {showDashboard && userProfile && (
          <Dashboard 
             user={userProfile} 
             onClose={() => setShowDashboard(false)} 
             onLogout={handleLogout}
             onOpenEditor={handleImageSelected}
             onConsumeCredit={consumeCredit}
             onContinueEditing={handleContinueEditing}
          />
      )}

      {showBuyCreditsModal && userProfile && (
          <BuyCreditsModal
            user={userProfile}
            initialProductId={pendingPlan}
            onClose={() => {
              setShowBuyCreditsModal(false);
              setPendingPlan(null);
            }}
          />
      )}

      {showReferrals && userProfile && (
          <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
      )}

      <ReferralPromoPopup
          client={supabase}
          userId={userProfile?.id}
          appSource="photoclear"
          onInvite={() => setShowReferrals(true)}
          suppressed={showReferrals || showBuyCreditsModal || showAdminPanel}
      />

      {/* Admin Panel */}
      {showAdminPanel && userProfile && isAdmin && adminRole && (
          <AdminPanel 
             user={userProfile} 
             adminRole={adminRole}
             onClose={() => navigateTo('/')} 
          />
      )}

      {/* Admin Button - Floating on Dashboard */}
      {showDashboard && userProfile && isAdmin && !showAdminPanel && (
          <button
            onClick={handleOpenAdmin}
            className="fixed bottom-20 right-4 z-[9999] bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
            Admin Panel
          </button>
      )}
      
      {/* Version Footer */}
      <div className="fixed bottom-2 right-2 text-[10px] text-slate-600 z-10">
        v{APP_VERSION}
      </div>
      
    </div>
  );
}

export default App;